import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import type { QuestionsApi } from '../api/questions'
import { QUESTION_SCHEMA_EXAMPLES } from './examples'
import { SchemaBlock } from './SchemaBlock'

/**
 * Questions > Schema: the shape a Question file has to take.
 *
 * The schema is FETCHED, from `/admin/questions/schema`: the API serves the embedded file the
 * validator is written against (app:R35), so the screen cannot drift from the validator. The
 * annotated comments in it carry every rule, which is why there is no field legend beside the
 * block: it would say each thing twice.
 *
 * One column, full width. That is what lets a MathML-heavy line be read without the block
 * scrolling sideways at the first `<msup>`.
 */
export function QuestionSchema({ api }: { api: QuestionsApi }) {
  // One block, paged, rather than four stacked. Four blocks made the page a wall of YAML.
  const [at, setAt] = useState(0)
  const example = QUESTION_SCHEMA_EXAMPLES[at]

  const schema = useQuery({
    // Long-lived on purpose: the schema changes when the API is deployed, not while
    // somebody is reading it.
    queryKey: ['question-schema'],
    queryFn: () => api.schema(),
    staleTime: Infinity,
  })

  function step(by: number) {
    const count = QUESTION_SCHEMA_EXAMPLES.length
    setAt((was) => (was + by + count) % count)
  }

  return (
    <>
      <h2 className="font-heading text-h2 font-bold text-heading">Question Schema</h2>

      <h3 className="mt-8 font-heading text-h4 font-bold text-heading">The schema</h3>
      {schema.isPending && <p className="mt-3 text-muted">Reading the schema...</p>}
      {schema.isError && (
        <p role="alert" className="mt-3 border-l-2 border-red-700 pl-3 text-red-700">
          {schema.error.message}
        </p>
      )}
      {schema.data && <SchemaBlock yaml={schema.data} className="mt-3" />}

      <h3 className="mt-10 font-heading text-h4 font-bold text-heading">Examples</h3>
      {example && (
        <>
          <SchemaBlock
            yaml={example.yaml}
            pager={{
              index: at,
              count: QUESTION_SCHEMA_EXAMPLES.length,
              label: example.title,
              onStep: step,
            }}
            className="mt-3"
          />
          <p className="mt-2 text-sm text-muted">{example.note}</p>
        </>
      )}
    </>
  )
}
